import {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  useLayoutEffect,
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import queryString from 'query-string'
import throttle from 'lodash/throttle'
import isNil from 'lodash/isNil'

// Redux Actions
import { setViewPort, setMapZoom } from '@/actions/index'

import { apiFetch } from '@/utilities/api'
import { apis } from '@/config/apiConfig'
import { useUser, getMapHomeLocation } from '@/queries/hooks/use-user'
import useWindowSize from '@/utilities/Hooks/useWindowSize'
import {
  hasValidHomeLocation,
  hasValidViewport,
  isValidLatitude,
  isValidLongitude,
  isValidZoom,
} from '@/utilities/mapViewport'

// Constants
import {
  TOC_WIDTH,
  SIDE_BAR_WIDTH,
  DEFAULT_HOME,
} from '@/containers/Map/Viewport/Constants'

import { BASIC_STYLE } from '@/mapStyles/basic/style'
import { getMapStyleJS } from '@/utilities/mapStyleUtils'
import { CLICKED_FEATURE } from '@/data/Layers/Auxiliary/ClickedFeature'
import { DELETE } from '@/data/Layers/Auxiliary/Delete'
import { DRAW } from '@/data/Layers/Auxiliary/Draw'
import { GEO_SPATIAL_RESULTS } from '@/data/Layers/Auxiliary/GeoSpatialResults'
import { MEASURE } from '@/data/Layers/Auxiliary/Measure'
import { MEASURE_CONTROL } from '@/data/Layers/Auxiliary/MeasureControl'
import { COORDINATE_SEARCH, GEOCODE } from '@/data/Layers/Auxiliary/Search'
import { SELECTED_FEATURES } from '@/data/Layers/Auxiliary/SelectedFeatures'
import { useSidebarUI } from '@/contexts/sidebar-ui-context'

type ViewportType = {
  latitude: number | null
  longitude: number | null
  zoom: number | null
  width?: number
  height?: number
  bearing?: number
  pitch?: number
  transitionDuration?: number
}

type RootState = {
  viewport: ViewportType | null
  mapStyle: unknown
}

type LocationPreferenceValue = {
  lat: number
  lng: number
  zoom: number
}

const PREFERENCE_UPDATE_THROTTLE = 3000 // 3 seconds

// Define the desired parameter order for URL query strings
const ORDERED_QUERY_KEYS = [
  'lat',
  'lng',
  'zoom',
  'baseStyle',
  'visibleLayers',
] as const

export const useMapUrlManager = () => {
  const location = useLocation()
  // NOTE: navigate identity is stable while the pathname stays the same
  // (react-router only recreates it on pathname changes), so search-only
  // replaces below do not reset the throttled updaters that depend on it.
  const navigate = useNavigate()
  const dispatch = useDispatch()

  // --- Selectors for necessary state ---
  const {
    data: user,
    isLoading: isUserLoading,
    isFetching: isUserFetching,
  } = useUser()
  const { tocCollapsed } = useSidebarUI()
  const reduxViewport = useSelector((state: RootState) => state.viewport)
  const mapStyle = useSelector((state: RootState) => state.mapStyle)

  const windowSize = useWindowSize({ debounceTime: 150 })

  // Refs for URL updates/persistence
  const isTransitioning = useRef(false)
  const viewportRef = useRef(reduxViewport)
  const prevVisibleLayersRef = useRef<string | null>(null)
  const previousLocationRef = useRef<LocationPreferenceValue | null>(null)
  const initialViewportSet = useRef(false)
  const [imageryRestored, setImageryRestored] = useState(false)

  // --- Initial Viewport Calculation Logic (from Viewport.tsx) ---
  const home = useMemo(() => {
    if (!user) return DEFAULT_HOME
    const homeLocation = getMapHomeLocation(user)
    return hasValidHomeLocation(homeLocation) ? homeLocation : DEFAULT_HOME
  }, [user])

  // Parse URL coordinates once and cache them
  const urlCoordinates = useMemo(() => {
    const params = queryString.parse(window.location.search)
    const lat = typeof params.lat === 'string' ? parseFloat(params.lat) : null
    const lng = typeof params.lng === 'string' ? parseFloat(params.lng) : null
    const zoom =
      typeof params.zoom === 'string' ? parseFloat(params.zoom) : null

    // Validate URL coordinates are reasonable numbers
    return isValidLatitude(lat) && isValidLongitude(lng) && isValidZoom(zoom)
      ? { latitude: lat, longitude: lng, zoom }
      : null
  }, [location.search]) // Only recalculate when URL actually changes

  const initialCoordinates = useMemo(() => {
    // Use URL coordinates if available (highest priority)
    if (urlCoordinates) {
      return urlCoordinates
    }

    // Fall back to user preferences with validation
    let lat = null
    let lng = null
    let zoom = null
    const prefs = user?.preferences
    if (prefs && typeof prefs === 'object' && 'location.history' in prefs) {
      const locationHistory = prefs['location.history']
      if (
        Array.isArray(locationHistory) &&
        locationHistory.length > 0 &&
        locationHistory[0]?.value
      ) {
        const defaultLocation = locationHistory[0].value

        // Validate and coerce coordinates with range checks
        const rawLat = defaultLocation.lat
        const rawLng = defaultLocation.lng
        const rawZoom = defaultLocation.zoom

        // Only assign if all values are valid and within proper ranges
        if (
          isValidLatitude(rawLat) &&
          isValidLongitude(rawLng) &&
          isValidZoom(rawZoom)
        ) {
          lat = rawLat
          lng = rawLng
          zoom = rawZoom
        }
      }
    }

    // Final fallback to home coordinates
    const initialLat = !isNil(lat) ? lat : home.x
    const initialLng = !isNil(lng) ? lng : home.y
    const initialZoom = !isNil(zoom) ? zoom : home.z
    return {
      latitude: initialLat,
      longitude: initialLng,
      zoom: initialZoom,
    }
  }, [urlCoordinates, home, user?.preferences])

  const sidebarWidth = useMemo(() => {
    return tocCollapsed ? 35 : TOC_WIDTH + SIDE_BAR_WIDTH
  }, [tocCollapsed])

  // --- Effect for Initial Viewport Dispatch ---
  useLayoutEffect(() => {
    if (initialViewportSet.current || !windowSize.width || !windowSize.height) {
      return
    }

    // If we don't have URL coordinates, we MUST wait for user data to be fully loaded
    // This prevents using DEFAULT_HOME when user preferences should be used instead
    if (!urlCoordinates) {
      if (isUserLoading || isUserFetching) {
        return
      }

      if (!user) {
        return
      }
    }

    const initialViewport: ViewportType = {
      ...initialCoordinates,
      bearing: 0,
      pitch: 0,
      width: windowSize.width - sidebarWidth,
      height: windowSize.height,
    }

    if (hasValidViewport(initialViewport)) {
      dispatch(setViewPort(initialViewport))
      dispatch(setMapZoom(initialViewport.zoom))
      initialViewportSet.current = true // Mark setup as done
    }
  }, [
    dispatch,
    initialCoordinates,
    windowSize,
    sidebarWidth,
    urlCoordinates,
    isUserLoading,
    isUserFetching,
    user,
  ])

  // --- Viewport Transition Tracking ---
  useEffect(() => {
    if (!reduxViewport) return undefined
    if (
      reduxViewport.transitionDuration &&
      reduxViewport.transitionDuration > 0
    ) {
      isTransitioning.current = true
      const timer = setTimeout(() => {
        isTransitioning.current = false
      }, reduxViewport.transitionDuration + 100)
      return () => clearTimeout(timer)
    }
    // Reset transition flag if duration becomes 0 or undefined
    isTransitioning.current = false
    return undefined
  }, [reduxViewport?.transitionDuration])

  // --- Helper function to create ordered query string ---
  const createOrderedQueryString = useCallback(
    (params: Record<string, string | number | boolean | undefined>) => {
      return queryString.stringify(params, {
        sort: (a, b) => {
          const indexA = ORDERED_QUERY_KEYS.findIndex(key => key === a)
          const indexB = ORDERED_QUERY_KEYS.findIndex(key => key === b)

          // If both keys are in orderedKeys, sort by their position
          if (indexA !== -1 && indexB !== -1) {
            return indexA - indexB
          }

          // If only one key is in orderedKeys, prioritize it
          if (indexA !== -1) return -1
          if (indexB !== -1) return 1

          // If neither key is in orderedKeys, sort alphabetically
          return a.localeCompare(b)
        },
      })
    },
    []
  )

  // Helper function to round zoom to nearest 0.25
  const roundToNearestQuarter = useCallback((value: number): string => {
    const rounded = Math.round(value * 4) / 4
    return rounded.toFixed(2)
  }, [])

  // --- Debounced URL Update for Viewport Coordinates ---
  const updateUrlWithViewport = useCallback(
    (lat: number, lng: number, zoom: number) => {
      if (isTransitioning.current) return

      // Read current URL at call time to avoid stale query params in throttled trailing calls
      const currentParams = queryString.parse(window.location.search)
      const newParams = {
        ...currentParams,
        lat: lat.toFixed(5),
        lng: lng.toFixed(5),
        zoom: roundToNearestQuarter(zoom),
      }
      const newSearch = createOrderedQueryString(newParams)

      // Replace the URL query without remounting the route (v5 history.replace equivalent)
      navigate({ search: newSearch }, { replace: true })
    },
    [navigate, createOrderedQueryString, roundToNearestQuarter]
  )

  const throttledUpdateUrl = useMemo(
    () =>
      throttle(updateUrlWithViewport, 1000, {
        leading: true,
        trailing: true,
      }),
    [updateUrlWithViewport]
  )

  // Effect to track viewport changes and update URL
  useEffect(() => {
    if (!reduxViewport || !viewportRef.current || isTransitioning.current) {
      // Update ref even if not updating URL, ensures comparison base is current
      viewportRef.current = reduxViewport
      return
    }

    const prev = viewportRef.current

    // Validate current viewport values before any calculations or URL updates
    if (!hasValidViewport(reduxViewport) || !hasValidViewport(prev)) {
      viewportRef.current = reduxViewport // Update ref if current or previous viewport is invalid
      return
    }

    const { latitude, longitude, zoom } = reduxViewport

    // Check for significant movement
    const latDiff = Math.abs(latitude - prev.latitude)
    const lngDiff = Math.abs(longitude - prev.longitude)
    const zoomDiff = Math.abs(zoom - prev.zoom)

    if (latDiff > 0.00001 || lngDiff > 0.00001 || zoomDiff > 0.01) {
      throttledUpdateUrl(latitude, longitude, zoom)
    }

    // Always update the ref for the next comparison
    viewportRef.current = reduxViewport
  }, [reduxViewport, throttledUpdateUrl]) // Depend on viewport and throttled updater

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      // @ts-ignore
      throttledUpdateUrl.cancel()
    }
  }, [throttledUpdateUrl])

  // --- URL Update for Visible Layers ---
  const visibleLayersString = useMemo(() => {
    if (!mapStyle) return null

    const baseLayers = BASIC_STYLE.layers.map(layer => layer.id)
    const ClickedFeature = CLICKED_FEATURE.layersArray.map(
      layerObj => layerObj.layer.id
    )
    const Delete = DELETE.layersArray.map(layerObj => layerObj.layer.id)
    const Draw = DRAW.layersArray.map(layerObj => layerObj.layer.id)
    const GeoSpatialResults = GEO_SPATIAL_RESULTS.layersArray.map(
      layerObj => layerObj.layer.id
    )
    const Measure = MEASURE.layersArray.map(layerObj => layerObj.layer.id)
    const MeasureControl = MEASURE_CONTROL.layersArray.map(
      layerObj => layerObj.layer.id
    )
    const Search = COORDINATE_SEARCH.layersArray.map(
      layerObj => layerObj.layer.id
    )
    const Geocode = GEOCODE.layersArray.map(layerObj => layerObj.layer.id)
    const SelectedFeatures = SELECTED_FEATURES.layersArray.map(
      layerObj => layerObj.layer.id
    )

    const excluded = [
      ...baseLayers,
      ...ClickedFeature,
      ...Delete,
      ...Draw,
      ...GeoSpatialResults,
      ...Measure,
      ...MeasureControl,
      ...Search,
      ...Geocode,
      ...SelectedFeatures,
      'measure_control_area_cold_outline',
    ]

    const style = getMapStyleJS(mapStyle) as {
      layers?: { id: string; layout?: { visibility?: string } }[]
    } | null
    if (!style?.layers) return null // Guard against invalid style object

    const { layers } = style
    const visible = layers
      .filter(layer => layer.layout?.visibility === 'visible')
      .filter(layer => !excluded.includes(layer.id))
      .map(layer => layer.id)

    return `[${visible.join(',')}]`
  }, [mapStyle])

  // Simple state-driven approach - capture URL layers once
  const initialUrlLayers = useRef<string | null>(null)
  if (initialUrlLayers.current === null) {
    const currentParams = queryString.parse(location.search)
    initialUrlLayers.current = (currentParams.visibleLayers as string) || null
  }

  // Helper to parse layer strings into arrays
  const parseLayerString = useCallback((layerString: string) => {
    return layerString
      .replace(/[[\]]/g, '')
      .split(',')
      .map(s => s.trim())
      .filter(Boolean)
  }, [])

  // Helper to extract imagery layers
  const getImageryLayers = useCallback((layers: string[]) => {
    return layers.filter(
      layer => layer.includes('aerial_') || layer.includes('imagery_')
    )
  }, [])

  // Track when initial imagery restoration completes so that subsequent
  // user-driven changes (e.g. selecting "None") are not blocked.
  // Uses state (not a ref) so that shouldAllowUrlUpdates recomputes reactively.
  useEffect(() => {
    if (imageryRestored) return
    if (!initialUrlLayers.current || !visibleLayersString) return

    const urlLayers = parseLayerString(initialUrlLayers.current)
    const urlImageryLayers = getImageryLayers(urlLayers)

    if (urlImageryLayers.length === 0) {
      if (urlLayers.length === 0) {
        // URL had visibleLayers=[] (user hid everything) — nothing to restore
        setImageryRestored(true)
        return
      }

      // URL had non-imagery layers but no imagery. Wait until at least one
      // of those layers appears in the map style, confirming InitMapStyle
      // has built the style. Without this, the gate unlocks while mapStyle
      // is still BASIC_STYLE, and visibleLayers=[] overwrites the URL.
      const currentLayers = parseLayerString(visibleLayersString)
      if (urlLayers.some(layerId => currentLayers.includes(layerId))) {
        setImageryRestored(true)
      }
      return
    }

    const currentLayers = parseLayerString(visibleLayersString)
    const currentImageryLayers = getImageryLayers(currentLayers)

    if (urlImageryLayers.every(layer => currentImageryLayers.includes(layer))) {
      setImageryRestored(true)
    }
  }, [visibleLayersString, imageryRestored, parseLayerString, getImageryLayers])

  // Gate URL updates until the initial imagery from the URL has been restored
  // into the map style. Once restored, imageryRestored flips to true and all
  // subsequent updates (including selecting "None") are allowed through.
  // imageryRestored is state (not a ref) so the useMemo recomputes reactively.
  //
  // NOTE: initialUrlLayers is a ref read inside useMemo. This is safe because
  // it is set synchronously during render (line ~390) so the value is always
  // current when the memo runs, and the only mutation path (user/map change
  // effect) also resets imageryRestored, which is a proper dependency and
  // triggers recomputation.
  const shouldAllowUrlUpdates = useMemo(() => {
    if (!initialUrlLayers.current || !visibleLayersString) return true
    return imageryRestored
  }, [visibleLayersString, imageryRestored])

  useEffect(() => {
    const currentParams = queryString.parse(location.search)

    if (
      visibleLayersString !== null &&
      visibleLayersString !== prevVisibleLayersRef.current &&
      shouldAllowUrlUpdates
    ) {
      prevVisibleLayersRef.current = visibleLayersString

      const newParams = {
        ...currentParams,
        visibleLayers: visibleLayersString,
      }
      const newSearch = createOrderedQueryString(newParams)

      navigate({ search: newSearch }, { replace: true })
    }
  }, [
    visibleLayersString,
    navigate,
    location.search,
    shouldAllowUrlUpdates,
    createOrderedQueryString,
  ])

  // Reset when user/map changes
  useEffect(() => {
    initialUrlLayers.current = null
    setImageryRestored(false)
  }, [user?.mapID, user?.accountID])

  // --- Throttled User Preference Saving ---
  const saveLocationPreference = useCallback((viewportData: ViewportType) => {
    if (!hasValidViewport(viewportData)) {
      return
    }

    const currentValue = {
      lat: viewportData.latitude,
      lng: viewportData.longitude,
      zoom: viewportData.zoom,
    }

    // Prevent saving if data is missing or unchanged
    if (
      JSON.stringify(previousLocationRef.current) ===
      JSON.stringify(currentValue)
    ) {
      return
    }

    apiFetch(
      `${apis.apiDatabase.uri}user/preferences/upsert`,
      'POST',
      {
        key: 'location.history',
        name: 'location',
        value: currentValue,
      },
      response => {
        if (response && response.success === false) {
          console.error(
            'Failed to save location preference:',
            response.message,
            { key: 'location.history', value: currentValue }
          )
        }
      }
    )

    previousLocationRef.current = currentValue
  }, [])

  const throttledSavePreference = useMemo(
    () =>
      throttle(saveLocationPreference, PREFERENCE_UPDATE_THROTTLE, {
        leading: false, // Don't save immediately on first change
        trailing: true, // Save after the throttle period ends
      }),
    [saveLocationPreference]
  )

  // Effect to trigger preference saving on viewport change
  useEffect(() => {
    // Only save if viewport is valid and user exists (implicitly needed for pref saving)
    if (
      user && // Ensure user context is available
      hasValidViewport(reduxViewport)
    ) {
      throttledSavePreference(reduxViewport)
    }
    // Add user dependency
  }, [reduxViewport, throttledSavePreference, user])

  // Cleanup throttle on unmount
  useEffect(() => {
    return () => {
      // @ts-ignore
      throttledSavePreference.cancel()
    }
  }, [throttledSavePreference])
}
